import { useState } from 'react'
import { STATUS_TYPE, StatusIdea, InteractionEvent, CuratedFolder } from 'interfaces'
import { Route, useLocation, Routes } from 'react-router'
import { TIMERANGE_FILTER_DEFAULT } from 'config'
import CuratedFolderViewHeader from './CuratedFolderViewHeader'
import CuratedFolderViewNavigation from './CuratedFolderViewNavigation'
import StatusesBricksView from './Statuses'
import CuratedFolderListView from './CuratedFolderListView'
import { FetchCuratedFolderPageContent } from 'components/Fetch'

import ErrorOverlay, {
  AJAX_ERROR_POPULAR_MESSAGE,
  NO_CONTENT_POPULAR_MESSAGE,
  AJAX_ERROR_STREAM_SUBTITLE
} from 'components/ErrorOverlay'
import { useIntl } from 'react-intl'

import styles from './CuratedFolderView.pcss'

export interface CuratedStatusesFolderViewProps {
  folder: CuratedFolder
  navigation: CuratedFolderViewNavigation
  hideHeader?: boolean
  forceGridView?: boolean
  trackInteraction: (categoryIds: string[], event: InteractionEvent) => Promise<any>
  onBackClick?: () => void
  onListViewScrolled?(folder: CuratedFolder, direction: 'left' | 'right'): void
  onItemShared?(folder: CuratedFolder, content: StatusIdea): void
}

export function CuratedStatusesFolderView(props: CuratedStatusesFolderViewProps) {
  const { folder, navigation, hideHeader, forceGridView, onItemShared, onListViewScrolled } = props
  const intl = useIntl()
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<StatusIdea[]>([])
  const [page, setPage] = useState(0)
  const [seed] = useState(Date.now().toString())
  const [error, setError] = useState<any>(undefined)


  const onFetched = (statuses: StatusIdea[]) => {
    setLoading(false)
    setItems((prevItems) => prevItems.concat(statuses))
  }

  const onFailed = (e: any) => {
    setLoading(false)
    setError(e)
  }

  const maybeLoadMore = () => {
    if (loading || error) {
      return
    }

    setLoading(true)
    setPage((prevPage) => prevPage + 1)
  }

  const renderList = () => {
    return [
      loading
        ? <FetchCuratedFolderPageContent
          key="fetch"
          folder={folder}
          page={page}
          statusesSeed={seed}
          range={TIMERANGE_FILTER_DEFAULT}
          onFetched={onFetched}
          onFailed={onFailed}
        />
        : null,

      <CuratedFolderListView
        key="items"
        stream={folder as any}
        items={items}
        loading={loading}
        onScrolled={onListViewScrolled as any}
        onItemShared={onItemShared as any}
        onScrollLimit={maybeLoadMore}
      />,

      items.length === 0
        ? renderError()
        : null
    ]
  }

  const renderError = () => {
    if (loading) {
      return null
    }

    if (error) {
      const err = AJAX_ERROR_POPULAR_MESSAGE(intl.formatMessage)
      const subtitle = AJAX_ERROR_STREAM_SUBTITLE(intl.formatMessage)

      return <ErrorOverlay originalError={error} error={err} subtitle={subtitle} className={styles.error} />
    }

    const err = NO_CONTENT_POPULAR_MESSAGE(STATUS_TYPE, intl.formatMessage)
    const subtitle = intl.formatMessage({ id: 'content.msg-no-content-curated-subtitle' })

    return <ErrorOverlay originalError={error} error={err} subtitle={subtitle} className={styles.error} />
  }

  const renderBricks = () => {
    return (
      <StatusesBricksView
        key="items"
        seed={seed}
        items={items}
        folder={folder as any}
        startPage={page + (loading ? 0 : 1)}
        trackInteraction={props.trackInteraction}
        onItemShared={onItemShared}
      />
    )
  }

  const onBackClick = () => {
    setLoading(true)
    setItems([])

    if (props.onBackClick) {
      props.onBackClick()
    }
  }

  const contentView = (
    <ContentView
      hideHeader={hideHeader}
      stream={folder as any}
      navigation={navigation}
      forceGridView={forceGridView}
      onBackClick={onBackClick}
      renderGrid={renderBricks}
      renderList={renderList}
    />
  )

  return (
    <Routes>
      <Route path=":category" element={contentView} />
      <Route path="/" element={contentView} />
    </Routes>
  )
}

function ContentView(props: {
  stream: CuratedFolder,
  navigation: any,
  forceGridView?: boolean,
  hideHeader?: boolean,
  onBackClick: () => void,
  renderGrid: () => React.ReactNode,
  renderList: () => React.ReactNode
}) {
  const location = useLocation()

  return (
    <section className={styles.folder}>
      {!props.hideHeader && (
        <CuratedFolderViewHeader
          stream={props.stream as any}
          navigation={props.navigation}
          onBackClick={props.onBackClick}
          expandedView={location.pathname !== props.navigation.prefix}
        />
      )}
      {
        location.pathname === props.navigation.prefix && !props.forceGridView
          ? props.renderList()
          : props.renderGrid()
      }
    </section>
  )
}

export default CuratedStatusesFolderView
